import { create } from "zustand";
import { persist } from "zustand/middleware";

// Höchstmenge je Position, wie im Backend (scripts/validation.js).
export const MAX_QUANTITY = 50;

const useCartStore = create(
  persist(
    (set, get) => ({
      cart: [], // Ensure cart is initialized as an array

      // Add an item to the cart
      addItem: (itemId, title, price, description, beilage = "", customerComment = "") =>
        set((state) => {
          // Ensure cart is an array before performing operations
          const cart = Array.isArray(state.cart) ? state.cart : [];
          const existingItem = cart.find(
            (item) => item.id === itemId && item.beilage === beilage
          );
          if (existingItem) {
            return {
              cart: cart.map((item) =>
                item.id === itemId && item.beilage === beilage
                  ? { ...item, quantity: Math.min(item.quantity + 1, MAX_QUANTITY) }
                  : item
              ),
            };
          }
          return {
            cart: [
              ...cart,
              {
                id: itemId,
                title,
                price,
                description,
                beilage,
                customerComment,
                quantity: 1,
              },
            ],
          };
        }),

      // Increase quantity of an item
      increaseQuantity: (itemId, beilage) =>
        set((state) => {
          const cart = Array.isArray(state.cart) ? state.cart : [];
          return {
            cart: cart.map((item) =>
              item.id === itemId && item.beilage === beilage
                ? { ...item, quantity: Math.min(item.quantity + 1, MAX_QUANTITY) }
                : item
            ),
          };
        }),

      // Beilage einer Position wechseln; gibt es die Kombination schon, werden
      // Menge (bis zur Höchstmenge) und Kommentare zusammengeführt.
      changeBeilage: (itemId, beilage, newBeilage) =>
        set((state) => {
          const cart = Array.isArray(state.cart) ? state.cart : [];
          const current = cart.find((item) => item.id === itemId && item.beilage === beilage);
          if (!current || beilage === newBeilage) return { cart };
          const target = cart.find((item) => item.id === itemId && item.beilage === newBeilage);
          if (!target) {
            return { cart: cart.map((item) => (item === current ? { ...item, beilage: newBeilage } : item)) };
          }
          const merged = {
            ...target,
            quantity: Math.min(target.quantity + current.quantity, MAX_QUANTITY),
            customerComment: [target.customerComment, current.customerComment].filter(Boolean).join("; "),
          };
          return {
            cart: cart.filter((item) => item !== current).map((item) => (item === target ? merged : item)),
          };
        }),

      // Decrease quantity of an item
      decreaseQuantity: (itemId, beilage) =>
        set((state) => {
          const cart = Array.isArray(state.cart) ? state.cart : [];
          return {
            cart: cart
              .map((item) =>
                item.id === itemId && item.beilage === beilage
                  ? { ...item, quantity: item.quantity - 1 }
                  : item
              )
              .filter((item) => item.quantity > 0),
          };
        }),

      // Remove an item from the cart
      removeItem: (itemId, beilage) =>
        set((state) => {
          const cart = Array.isArray(state.cart) ? state.cart : [];
          return {
            cart: cart.filter(
              (item) => !(item.id === itemId && item.beilage === beilage)
            ),
          };
        }),

      // Total items in the cart
      totalItems: () => {
        const cart = Array.isArray(get().cart) ? get().cart : [];
        return cart.reduce((total, item) => total + item.quantity, 0);
      },

      // Total price of items in the cart
      totalPrice: () => {
        const cart = Array.isArray(get().cart) ? get().cart : [];
        return cart.reduce((total, item) => total + item.price * item.quantity, 0);
      },
      updateCustomerComment: (itemId, beilage, customerComment) =>
        set((state) => {
          const cart = Array.isArray(state.cart) ? state.cart : [];
          return {
            cart: cart.map((item) =>
              item.id === itemId && item.beilage === beilage
                ? { ...item, customerComment }
                : item
            ),
          };
        }),
        updateItem: (itemId, beilage, updatedProperties) =>
          set((state) => {
            const cart = Array.isArray(state.cart) ? state.cart : [];
            return {
              cart: cart.map((item) =>
                item.id === itemId && item.beilage === beilage
                  ? { ...item, ...updatedProperties }
                  : item
              ),
            };
          }),
        
    }),
    {
      name: "cart-storage", // Key in localStorage
    }
  )
);

export default useCartStore;
